import React from 'react';
import { RefreshCcw } from 'lucide-react';
import MultiSelectDropdownInline from '../multi-select-inline';
import { FilterState } from './types';

interface JobsiteListByOrganizerFiltersProps {
  activeFilters: FilterState;
  setActiveFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  provinces: { id: string; name: string }[];
  localUnions: { id: string; name: string }[];
  statusOptions: { id: string; name: string }[];
  unionStatusOptions: { id: string; name: string }[];
  organizers: { id: string; name: string; role: { id: string; roleName: string } }[];
  handleApplyFilters: () => void;
  handleFilterClearAll: () => void;
  isJobsiteListByOrganizerFiltersValid: () => boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const JobsiteListByOrganizerFilters: React.FC<JobsiteListByOrganizerFiltersProps> = ({
  activeFilters,
  setActiveFilters,
  provinces,
  localUnions,
  statusOptions,
  unionStatusOptions,
  organizers,
  handleApplyFilters,
  handleFilterClearAll,
  isJobsiteListByOrganizerFiltersValid,
  setLoading
}) => {
  return (
    <div className="bg-white rounded-[12px] shadow p-3 sm:p-4 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
        {/* Start Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Start Date</label>
          <input
            id="organizer-report-start-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 md:w-[95%]"
            value={activeFilters.startDate ? activeFilters.startDate.split('T')[0] : ''}
            max={activeFilters.endDate ? activeFilters.endDate.split('T')[0] : undefined}
            onChange={(e) => {
              setActiveFilters(prev => ({
                ...prev,
                startDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* End Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">End Date</label>
          <input
            id="organizer-report-end-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 md:w-[95%]"
            value={activeFilters.endDate ? activeFilters.endDate.split('T')[0] : ''}
            min={activeFilters.startDate ? activeFilters.startDate.split('T')[0] : undefined}
            onChange={(e) => {
              setActiveFilters(prev => ({
                ...prev,
                endDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* Organizer Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Organizer Name</label>
          <div id="organizer-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="organizer-dropdown"
              options={organizers.filter(organizer => organizer.role.roleName === "organizer")}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  organizer: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.organizer || []}
              placeholder="Select organizers"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Jobsite Status Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Jobsite Status</label>
          <div id="organizer-report-status-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="organizer-report-status-dropdown"
              options={statusOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  status: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.status}
              placeholder="Select statuses"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Province Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Province</label>
          <div id="organizer-report-province-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="organizer-report-province-dropdown"
              options={provinces}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  province: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.province}
              placeholder="Select provinces"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Union Status Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Union Status</label>
          <div id="monthly-union-status-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="monthly-union-status-dropdown"
              options={unionStatusOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  unionStatus: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.unionStatus || []}
              placeholder="Select union status"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Local Unions Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Local Union</label>
          <div id="local-unions-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="local-unions-dropdown"
              options={localUnions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  localUnion: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.localUnion || []}
              placeholder="Select local unions"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>
        {/* Apply Button */}
        <div className="w-full sm:col-span-2 lg:col-span-3 xl:col-span-6 flex justify-end mt-4 gap-2">
          <button
            id="organizer-report-apply-filters-button"
            className={`btn-primary whitespace-nowrap flex-1 sm:flex-none ${!isJobsiteListByOrganizerFiltersValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleApplyFilters}
            disabled={!isJobsiteListByOrganizerFiltersValid()}
          >
            Apply Filters
          </button>
          <button
            id="organizer-report-refresh-button"
            className="btn-secondary whitespace-nowrap"
            onClick={() => {
              setLoading(true);
              handleFilterClearAll();
            }}
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsiteListByOrganizerFilters;
